import React, { useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Button, Modal, theme } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'
import ContactForm from './contact-form'

const { useToken } = theme

const PricingContainer = styled.div`
  width: 100%;
  padding: 0 ${({ token }) => token.padding}px;
`

const PricingRow = styled(Row)`
  display: flex;
  gap: ${({ token }) => token.margin}px;
  flex-wrap: no-wrap;
  justify-content: center;
`

const PricingColumn = styled(Col)`
  &.pricing-table-item {
    background: ${({ token }) => token.colorBgContainer};
    border: 1px solid ${({ token }) => token.colorBorder};
    border-radius: ${({ token }) => token.borderRadius}px;
    padding: ${({ token }) => token.padding}px;
    text-align: center;
    display: flex;
    flex-direction: column;
    justify-content: space-between;
    min-height: 300px;
    width: 100%;
    max-width: 300px;

    @media (max-width: ${({ token }) => token.screenSM}px) {
      min-height: 280px;
      padding: ${({ token }) => token.paddingSM}px;
    }

    @media (max-width: ${({ token }) => token.screenXS}px) {
      min-height: 160px;
      padding: ${({ token }) => token.paddingXS}px;
    }
  }

  .pricing-table-item-title {
    color: ${({ token }) => token.colorText};
    font-size: ${({ token }) => token.fontSizeHeading3}px;
    font-weight: 600;

    @media (max-width: ${({ token }) => token.screenSM}px) {
      font-size: ${({ token }) => token.fontSizeHeading4}px;
    }

    @media (max-width: ${({ token }) => token.screenXS}px) {
      font-size: ${({ token }) => token.fontSizeHeading5}px;
    }
  }

  .pricing-table-item-info {
    color: ${({ token }) => token.colorTextSecondary};
    font-size: ${({ token }) => token.fontSize}px;
    line-height: 1.5;
    margin-bottom: ${({ token }) => token.marginLG}px;

    @media (max-width: ${({ token }) => token.screenSM}px) {
      font-size: ${({ token }) => token.fontSizeSM}px;
      margin-bottom: ${({ token }) => token.margin}px;
    }

    @media (max-width: ${({ token }) => token.screenXS}px) {
      font-size: ${({ token }) => token.fontSizeSM}px;
      margin-bottom: ${({ token }) => token.marginSM}px;
    }
  }

  .feature-item {
    display: flex;
    align-items: center;
    gap: ${({ token }) => token.marginXS}px;
    margin-bottom: ${({ token }) => token.marginXS}px;
    text-align: left;

    .anticon {
      color: ${({ token }) => token.colorSuccess};
      font-size: ${({ token }) => token.fontSize}px;

      @media (max-width: ${({ token }) => token.screenSM}px) {
        font-size: ${({ token }) => token.fontSizeSM}px;
      }
    }

    p {
      margin: 0;
      color: ${({ token }) => token.colorTextSecondary};
      font-size: ${({ token }) => token.fontSize}px;

      @media (max-width: ${({ token }) => token.screenSM}px) {
        font-size: ${({ token }) => token.fontSizeSM}px;
      }
    }
  }
`

const StyledButton = styled(Button)`
  &.tuba-primary-button {
    border-radius: 30px;
    background-image: linear-gradient(70.98762166149521deg, #008080 0%, #02b7b7 100%);
    border: none;
    color: #ffffff;
    font-weight: 600;
    height: 48px;
    font-size: 16px;
    width: 100%;
    margin: 0 auto;

    @media (max-width: ${({ token }) => token.screenSM}px) {
      height: 44px;
      font-size: 14px;
    }

    @media (max-width: ${({ token }) => token.screenXS}px) {
      height: 40px;
      font-size: 13px;
    }

    &:hover {
      box-shadow: 1px 1px 5px 0px rgba(0, 0, 0, 0.35);
      background-image: linear-gradient(70.98762166149521deg, #008080 0%, #02b7b7 100%);
    }

    &:disabled {
      background-image: linear-gradient(70.98762166149521deg, #d9d9d9 0%, #bfbfbf 100%);
      color: #8c8c8c;
      cursor: not-allowed;
    }
  }
`

const ResponsiveModal = styled(Modal)`
  @media (max-width: ${({ token }) => token.screenSM}px) {
    .ant-modal {
      margin: 16px;
      max-width: calc(100vw - 32px);
    }

    .ant-modal-content {
      padding: 16px;
    }
  }

  @media (max-width: ${({ token }) => token.screenXS}px) {
    .ant-modal {
      margin: 8px;
      max-width: calc(100vw - 16px);
    }

    .ant-modal-content {
      padding: 12px;
    }
  }
`

/**
 * PricingTable component for displaying subscription plans
 * @param {Object} props - Component props
 * @param {Array} props.subscriptionTiers - Available subscription tiers
 * @param {Array} props.subscriptionCycles - Available subscription cycles
 * @param {Function} props.onPurchase - Function to handle trial purchase
 * @param {Function} props.onSend - Function to handle contact form submission
 * @param {Object} props.activeTier - Currently active subscription tier
 * @param {Array} props.organizationSubscriptions - Organization's subscriptions
 * @returns {JSX.Element} Rendered pricing table
 */
const PricingTable = ({
  subscriptionTiers,
  subscriptionCycles,
  onPurchase,
  onSend,
  activeTier,
  organizationSubscriptions,
}) => {
  const { t } = useTranslation()
  const { token } = useToken()
  const [isModalVisible, setIsModalVisible] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const isSubscription = !!activeTier

  console.log('organizationSubscriptions', subscriptionTiers)

  const tiers = subscriptionTiers || []
  const cycles = subscriptionCycles || []
  const normalTier = tiers.find(tier => tier.tierType === 'NORMAL')
  const hadTrialSubscription =
    organizationSubscriptions?.some(subscription => subscription.isTrial === true) || false

  const paymentPendingSubscription = organizationSubscriptions?.length
    ? organizationSubscriptions.find(sub => sub.state === 'PAYMENT_PENDING')
    : null

  if (!tiers.length || !cycles.length) {
    return <div>{t('common.loading')}</div>
  }

  const mockedFreeTierFeatures =
    tiers[tiers.length - 1]?.features?.map((f, index) => {
      return (
        <Row key={index} className="feature-item" align="middle">
          <CheckOutlined />
          <p>
            {f.featureMessage
              .replace('locations', 'custom segmentation 1')
              .replace('departments', 'custom segmentation 2')
              .replace('products', 'measurement sets')
              .replace('admins', 'products')}
          </p>
        </Row>
      )
    }) || []

  mockedFreeTierFeatures.pop()

  const showModal = () => {
    setIsModalVisible(true)
  }

  const handleCancel = () => {
    setIsModalVisible(false)
  }

  const handleFormSubmit = formData => {
    const data = {
      subscription_tier_id: normalTier.id,
      subscription_cycle_id: 1,
      organization: formData.organization,
      email: formData.email,
      job_title: formData.jobTitle,
      phone: formData.phone || '',
      help_message: formData.message || '',
      first_name: formData.name,
      last_name: formData.lastName,
    }

    onSend(data)
    setIsSubmitted(true)
    setIsModalVisible(false)
  }

  return (
    <PricingContainer id="pricing" token={token}>
      <PricingRow justify="center" token={token}>
        <PricingColumn
          className="pricing-table-item"
          xs={24}
          sm={24}
          md={12}
          lg={12}
          xl={12}
          token={token}
        >
          <h2 className="pricing-table-item-title">{t('subscriptions.freeTrialTitle')}</h2>
          <p className="pricing-table-item-info" style={{ flex: 1 }}>
            {t('subscriptions.freeTrialInfo', {
              duration: subscriptionTiers.find(tier => tier?.tierType === 'TRIAL')
                ?.fixedDurationDays,
            })}
          </p>
          <StyledButton
            className="tuba-primary-button"
            onClick={() => onPurchase(1, 1)}
            token={token}
            {...((isSubscription || hadTrialSubscription) && { type: 'primary', disabled: true })}
          >
            {hadTrialSubscription ? t('subscriptions.hadTrial') : t('subscriptions.buttonDemo')}
          </StyledButton>
        </PricingColumn>
        <PricingColumn
          className="pricing-table-item"
          xs={24}
          sm={24}
          md={12}
          lg={12}
          xl={12}
          token={token}
        >
          {!isSubmitted ? (
            <>
              <h2 className="pricing-table-item-title">{t('subscriptions.professionalPlan')}</h2>
              <p className="pricing-table-item-info" style={{ flex: 1 }}>
                {t('subscriptions.professionalPlanInfo')}
              </p>
              <StyledButton
                className="tuba-primary-button"
                onClick={showModal}
                token={token}
                {...(((isSubscription && activeTier.tierType === 'NORMAL') ||
                  paymentPendingSubscription) && {
                  type: 'primary',
                  disabled: true,
                })}
              >
                {paymentPendingSubscription
                  ? t('subscriptions.paymentPending')
                  : t('subscriptions.contactUs')}
              </StyledButton>
            </>
          ) : (
            <>
              <h2 className="pricing-table-item-title">{t('subscriptions.professionalPlan')}</h2>
              <p className="pricing-table-item-info">
                {t('subscriptions.form.successDescription')}
              </p>
            </>
          )}
          <ResponsiveModal
            title={t('subscriptions.form.title')}
            open={isModalVisible}
            onCancel={handleCancel}
            footer={null}
            width={600}
            token={token}
            centered
            destroyOnHidden
          >
            <ContactForm onSubmit={handleFormSubmit} />
          </ResponsiveModal>
        </PricingColumn>
      </PricingRow>
    </PricingContainer>
  )
}

PricingTable.defaultProps = {
  subscriptionTiers: [],
  subscriptionCycles: [],
  activeTier: null,
  organizationSubscriptions: [],
}

PricingTable.propTypes = {
  subscriptionTiers: PropTypes.array.isRequired,
  subscriptionCycles: PropTypes.array.isRequired,
  onPurchase: PropTypes.func.isRequired,
  onSend: PropTypes.func.isRequired,
  activeTier: PropTypes.object,
  organizationSubscriptions: PropTypes.array,
}

export default PricingTable
